/**
 * Client File Management Types
 * Frontend types for client and folder management
 */

export type ClientType = 'individual' | 'company' | 'trust' | 'smsf' | 'partnership';

export type ClientStatus = 'active' | 'inactive' | 'archived';

// Deadline types for compliance tracking
export type DeadlineType = 'bas' | 'income_tax' | 'fbt' | 'payg' | 'smsf' | 'company_return' | 'trust_return' | 'custom';

export type DeadlineStatus = 'upcoming' | 'due_soon' | 'overdue' | 'completed';

export type RecurrencePattern = 'none' | 'monthly' | 'quarterly' | 'annually';

// Document types
export type DocumentStatus = 'active' | 'archived' | 'deleted';
export type DocumentCategory = 'tax_return' | 'bas' | 'financial_statement' | 'correspondence' | 'contract' | 'receipt' | 'invoice' | 'other';

export type FolderType =
  | 'client_root'
  | 'tax_returns'
  | 'bas'
  | 'financials'
  | 'correspondence'
  | 'workpapers'
  | 'general';

export interface WorkHistoryItem {
  date: string;
  description: string;
  type: 'tax_lodgement' | 'bas_lodgement' | 'meeting' | 'correspondence' | 'audit' | 'review' | 'other';
}

export interface Client {
  id: string;
  tenantId: string;
  clientCode: string;
  displayName: string;
  clientType: ClientType;
  email?: string;
  phone?: string;
  address?: string;
  abn?: string;
  acn?: string;
  tfn?: string;
  industry?: string;
  totalDocuments: number;
  totalSizeBytes: number;
  indexedDocuments: number;
  aiSummary?: string;
  serviceDescription?: string;
  workHistory?: WorkHistoryItem[];
  keyDates?: KeyDate[];
  tags?: string[];
  status: ClientStatus;
  healthScore?: ClientHealthScore;
  // Xero integration fields
  xeroContactId?: string;
  xeroContactName?: string;
  xeroOrgId?: string;
  xeroOrgName?: string;
  xeroLastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Client Health Score types
export type HealthScoreLevel = 'critical' | 'needs_attention' | 'good' | 'excellent';

export interface ClientHealthScore {
  score: number; // 0-100
  level: HealthScoreLevel;
  lastCalculated: string;
  issues: HealthIssue[];
  missingDocuments: string[];
  expiredDocuments: ExpiredDocument[];
  complianceRisks: ComplianceRisk[];
  recommendations: string[];
}

export interface HealthIssue {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  category: 'documentation' | 'compliance' | 'consistency' | 'expiry';
  title: string;
  description: string;
  impact: string;
  recommendation: string;
  createdAt: string;
}

export interface ExpiredDocument {
  documentName: string;
  expiryDate: string;
  daysOverdue: number;
  documentType: string;
}

export interface ComplianceRisk {
  riskType: 'ato_audit' | 'smsf_compliance' | 'gst_inconsistency' | 'missing_lodgement' | 'other';
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  affectedPeriod?: string;
  estimatedImpact?: string;
}

// Action List types
export type ActionPriority = 'urgent' | 'high' | 'medium' | 'low';
export type ActionCategory = 'id_renewal' | 'document_expiry' | 'compliance' | 'lodgement' | 'review' | 'follow_up';
export type ActionStatus = 'pending' | 'in_progress' | 'completed' | 'dismissed';

export interface ActionItem {
  id: string;
  clientId: string;
  clientName: string;
  clientType: ClientType;
  category: ActionCategory;
  priority: ActionPriority;
  title: string;
  description: string;
  dueDate?: string;
  estimatedTime?: number; // minutes
  status: ActionStatus;
  assignedTo?: string;
  relatedDocumentIds?: string[];
  createdAt: string;
  updatedAt: string;
}

// Revenue Opportunity types
export type OpportunityType = 'advisory' | 'planning' | 'restructure' | 'investment' | 'compliance_upgrade' | 'additional_service';
export type OpportunityStatus = 'identified' | 'contacted' | 'proposal_sent' | 'won' | 'lost' | 'dismissed';

export interface RevenueOpportunity {
  id: string;
  clientId: string;
  clientName: string;
  clientType: ClientType;
  opportunityType: OpportunityType;
  title: string;
  description: string;
  reasoning: string; // AI-generated explanation
  estimatedValue: number; // in AUD
  confidence: number; // 0-100
  status: OpportunityStatus;
  identifiedAt: string;
  identifiedBy: 'ai' | 'manual';
  relatedDocumentIds?: string[];
  nextSteps?: string[];
  contactedAt?: string;
  wonAt?: string;
  actualValue?: number;
  createdAt: string;
  updatedAt: string;
}

export interface KeyDate {
  date: string;
  label: string;
  type: 'deadline' | 'lodgement' | 'anniversary' | 'custom';
}

// Deadline interface for compliance tracking
export interface Deadline {
  id: string;
  tenantId: string;
  clientId: string;
  clientName?: string;
  title: string;
  description?: string;
  deadlineType: DeadlineType;
  status: DeadlineStatus;
  dueDate: string;
  completedAt?: string;
  lodgementReference?: string;
  recurrence: RecurrencePattern;
  reminderDays: number[];
  linkedDocumentIds?: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Folder {
  id: string;
  tenantId: string;
  clientId?: string;
  parentFolderId?: string;
  name: string;
  fullPath: string;
  s3Prefix: string;
  folderType: FolderType;
  documentCount: number;
  subfolderCount: number;
  totalSizeBytes: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClientDocument {
  id: string;
  tenantId: string;
  clientId?: string;
  folderId?: string;
  userCognitoSub: string;
  filename: string;
  originalFilename: string;
  fileType: string;
  fileSizeBytes: number;
  mimeType?: string;
  s3Bucket: string;
  s3Key: string;
  documentCategory?: string;
  financialYear?: string;
  aiSummary?: string;
  bedrockStatus: string;
  bedrockIndexedAt?: string;
  description?: string;
  tags?: string[];
  // Extracted metadata fields
  extractedEntities?: ExtractedEntities | null;
  extractedText?: string | null;
  documentDate?: string | null;
  isScanned?: boolean;
  pageCount?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExtractedEntities {
  names?: string[];
  clientName?: string;
  abn?: string;
  acn?: string;
  tfn?: string;
  amounts?: { value: number; label: string }[];
  totalAmount?: number;
  referenceNumbers?: { number: string; type: string }[];
  accountNumbers?: string[];
  period?: string;
  payee?: string;
  payer?: string;
  address?: string;
  keyDates?: { date: string; label: string }[];
  documentYear?: string;
  keyContent?: string;
}

export interface EmbeddingChunk {
  chunkIndex: number;
  chunkText: string;
  createdAt: string;
}

export interface FileMetadataResponse {
  file: ClientDocument & { processingStatus: string };
  chunks: EmbeddingChunk[];
  totalChunks: number;
}

export interface FolderBreadcrumb {
  id: string;
  name: string;
  fullPath: string;
}

// Enhanced Document interfaces
export interface DocumentVersion {
  id: string;
  documentId: string;
  versionNumber: number;
  filename: string;
  fileSizeBytes: number;
  s3Key: string;
  uploadedBy: string;
  uploadedByName: string;
  changeDescription?: string;
  createdAt: string;
}

export interface DocumentTemplate {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  category: DocumentCategory;
  filename: string;
  fileType: string;
  fileSizeBytes: number;
  s3Key: string;
  thumbnailUrl?: string;
  isActive: boolean;
  usageCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentMetadata {
  id: string;
  documentId: string;
  extractedText?: string;
  extractedABN?: string;
  extractedACN?: string;
  extractedDate?: string;
  extractedAmount?: number;
  extractedInvoiceNumber?: string;
  confidence?: number;
  extractedAt?: string;
}

export interface DocumentShare {
  id: string;
  documentId: string;
  sharedWith: string;
  sharedByName: string;
  expiresAt?: string;
  accessLevel: 'view' | 'download' | 'edit';
  createdAt: string;
}

export interface FolderContents {
  folder: Folder | null;
  subfolders: Folder[];
  documents: ClientDocument[];
  breadcrumbs: FolderBreadcrumb[];
}

// Request types

export interface CreateClientRequest {
  clientCode: string;
  displayName: string;
  clientType?: ClientType;
  email?: string;
  phone?: string;
  address?: string;
  abn?: string;
  acn?: string;
  tfn?: string;
  industry?: string;
  tags?: string[];
  status?: ClientStatus;
}

export interface UpdateClientRequest {
  clientCode?: string;
  displayName?: string;
  clientType?: ClientType;
  email?: string;
  phone?: string;
  address?: string;
  abn?: string;
  acn?: string;
  tfn?: string;
  industry?: string;
  aiSummary?: string;
  keyDates?: KeyDate[];
  tags?: string[];
  status?: ClientStatus;
}

export interface CreateFolderRequest {
  clientId?: string;
  parentFolderId?: string;
  name: string;
  folderType?: FolderType;
}

export interface UpdateFolderRequest {
  name?: string;
  folderType?: FolderType;
}

export interface CreateDeadlineRequest {
  clientId: string;
  title: string;
  description?: string;
  deadlineType: DeadlineType;
  dueDate: string;
  recurrence?: RecurrencePattern;
  reminderDays?: number[];
  notes?: string;
}

export interface UpdateDeadlineRequest {
  title?: string;
  description?: string;
  deadlineType?: DeadlineType;
  status?: DeadlineStatus;
  dueDate?: string;
  completedAt?: string;
  lodgementReference?: string;
  recurrence?: RecurrencePattern;
  reminderDays?: number[];
  linkedDocumentIds?: string[];
  notes?: string;
}

export interface DeadlineListFilters {
  clientId?: string;
  status?: DeadlineStatus | DeadlineStatus[];
  deadlineType?: DeadlineType | DeadlineType[];
  dueDateFrom?: string;
  dueDateTo?: string;
  search?: string;
  sortBy?: 'dueDate' | 'clientName' | 'title' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Document request/response types
export interface DocumentListFilters {
  clientId?: string;
  folderId?: string;
  category?: DocumentCategory | DocumentCategory[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'filename' | 'createdAt' | 'fileSizeBytes' | 'category';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface DocumentListResponse {
  documents: ClientDocument[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  category: DocumentCategory;
  file: File;
}

export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  category?: DocumentCategory;
  isActive?: boolean;
}

// Filter types

export interface ClientListFilters {
  status?: ClientStatus | ClientStatus[];
  clientType?: ClientType | ClientType[];
  search?: string;
  industry?: string;
  tags?: string[];
  sortBy?: 'displayName' | 'clientCode' | 'createdAt' | 'updatedAt' | 'totalDocuments';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface FolderListFilters {
  clientId?: string;
  parentFolderId?: string | null;
  folderType?: FolderType;
  search?: string;
  sortBy?: 'name' | 'createdAt' | 'documentCount';
  sortOrder?: 'asc' | 'desc';
}

// Response types

export interface ClientListResponse {
  clients: Client[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ClientStats {
  totalClients: number;
  activeClients: number;
  inactiveClients: number;
  archivedClients: number;
  clientsByType: Record<ClientType, number>;
  totalDocuments: number;
  totalSizeBytes: number;
  indexedDocuments: number;
}

export interface FolderStats {
  totalFolders: number;
  foldersByType: Record<FolderType, number>;
  totalDocuments: number;
  totalSizeBytes: number;
}

export interface DeadlineListResponse {
  deadlines: Deadline[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DeadlineStats {
  totalDeadlines: number;
  upcoming: number;
  dueSoon: number;
  overdue: number;
  completed: number;
  byType: Record<DeadlineType, number>;
}

// Global search types
export interface GlobalSearchResult {
  type: 'client' | 'document' | 'deadline' | 'folder';
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  clientId?: string;
  clientName?: string;
  matchedField?: string;
  score?: number;
}

export interface GlobalSearchResponse {
  results: GlobalSearchResult[];
  total: number;
  query: string;
}

// Search result for autocomplete
export interface ClientSearchResult {
  id: string;
  clientCode: string;
  displayName: string;
  clientType: ClientType;
}

// Helper type for displaying client type labels
export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  individual: 'Individual',
  company: 'Company',
  trust: 'Trust',
  smsf: 'SMSF',
  partnership: 'Partnership',
};

// Helper type for displaying folder type labels
export const FOLDER_TYPE_LABELS: Record<FolderType, string> = {
  client_root: 'Client Root',
  tax_returns: 'Tax Returns',
  bas: 'BAS',
  financials: 'Financials',
  correspondence: 'Correspondence',
  workpapers: 'Workpapers',
  general: 'General',
};

// Helper type for displaying client status labels
export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
};

// Helper type for displaying deadline type labels
export const DEADLINE_TYPE_LABELS: Record<DeadlineType, string> = {
  bas: 'BAS',
  income_tax: 'Income Tax Return',
  fbt: 'FBT',
  payg: 'PAYG',
  smsf: 'SMSF',
  company_return: 'Company Return',
  trust_return: 'Trust Return',
  custom: 'Custom',
};

// Helper type for displaying deadline status labels
export const DEADLINE_STATUS_LABELS: Record<DeadlineStatus, string> = {
  upcoming: 'Upcoming',
  due_soon: 'Due Soon',
  overdue: 'Overdue',
  completed: 'Completed',
};

// Helper type for displaying document category labels
export const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  tax_return: 'Tax Return',
  bas: 'BAS',
  financial_statement: 'Financial Statement',
  correspondence: 'Correspondence',
  contract: 'Contract',
  receipt: 'Receipt',
  invoice: 'Invoice',
  bank_statement: 'Bank Statement',
  workpaper: 'Workpaper',
  payroll: 'Payroll',
  superannuation: 'Superannuation',
  asic: 'ASIC',
  insurance: 'Insurance',
  lease: 'Lease',
  loan: 'Loan',
  other: 'Other',
};

// Metadata extraction types
export interface MetadataProcessingStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  skipped: number;
  scannedCount: number;
  categories: Record<string, number>;
}

export interface FailedFile {
  id: string;
  filename: string;
  error: string;
  fileSize: number;
  updatedAt: string;
}

export interface BatchProcessingResult {
  processed: number;
  failed: number;
  skipped: number;
  remaining: number;
  results: Array<{
    fileId: string;
    filename: string;
    success: boolean;
    isScanned: boolean;
    pageCount?: number;
    category?: string;
    error?: string;
  }>;
}

// Helper type for displaying recurrence pattern labels
export const RECURRENCE_LABELS: Record<RecurrencePattern, string> = {
  none: 'One-time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
};

// Helper function to format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Risk Heatmap types
export type RiskCategory = 'compliance' | 'documentation' | 'expiry' | 'financial' | 'operational';
export type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'none';

export interface ClientRiskData {
  clientId: string;
  clientName: string;
  clientType: ClientType;
  overallRisk: RiskLevel;
  healthScore: number;
  riskBreakdown: Record<RiskCategory, RiskLevel>;
  criticalIssues: number;
  highIssues: number;
  mediumIssues: number;
  lastAssessed: string;
}

export interface RiskHeatmapStats {
  totalClients: number;
  criticalRisk: number;
  highRisk: number;
  mediumRisk: number;
  lowRisk: number;
  noRisk: number;
  averageHealthScore: number;
  totalIssues: number;
}

// Client Timeline types
export type TimelineEventType =
  | 'document_upload'
  | 'deadline_created'
  | 'deadline_completed'
  | 'meeting'
  | 'email'
  | 'phone_call'
  | 'tax_lodgement'
  | 'bas_lodgement'
  | 'note'
  | 'status_change'
  | 'health_score_change'
  | 'issue_detected'
  | 'issue_resolved';

export interface TimelineEvent {
  id: string;
  clientId: string;
  eventType: TimelineEventType;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  performedBy?: string;
  timestamp: string;
  icon?: string;
  color?: string;
}

export interface ClientTimeline {
  clientId: string;
  clientName: string;
  events: TimelineEvent[];
  total: number;
}

// Regulatory Change Impact types
export type RegulatoryCategory = 'tax' | 'gst' | 'smsf' | 'ato' | 'asic' | 'corporate' | 'trust';
export type ImpactSeverity = 'critical' | 'high' | 'medium' | 'low';
export type ImpactStatus = 'new' | 'reviewed' | 'actioned' | 'dismissed';

export interface RegulatoryChange {
  id: string;
  title: string;
  category: RegulatoryCategory;
  description: string;
  effectiveDate: string;
  publishedDate: string;
  source: string; // e.g., "ATO Ruling", "Legislation", "ASIC Guidance"
  sourceUrl?: string;
  severity: ImpactSeverity;
  affectedClients: number;
  estimatedRevenue: number;
  keyChanges: string[];
  requiredActions: string[];
}

export interface ClientImpact {
  clientId: string;
  clientName: string;
  clientType: ClientType;
  regulationId: string;
  regulationTitle: string;
  severity: ImpactSeverity;
  status: ImpactStatus;
  reasoning: string;
  affectedAreas: string[];
  recommendedActions: string[];
  estimatedHours: number;
  estimatedValue: number;
  identifiedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  notes?: string;
}

export interface RegulatoryImpactStats {
  totalRegulations: number;
  newThisMonth: number;
  totalAffectedClients: number;
  criticalImpacts: number;
  highImpacts: number;
  totalEstimatedRevenue: number;
  unreviewedImpacts: number;
}

// Document Consistency types
export type InconsistencyType =
  | 'missing_document'
  | 'data_mismatch'
  | 'cross_year_inconsistency'
  | 'missing_supporting_doc'
  | 'incomplete_set'
  | 'expired_reference';

export type InconsistencySeverity = 'critical' | 'high' | 'medium' | 'low';

export interface DocumentInconsistency {
  id: string;
  clientId: string;
  clientName: string;
  clientType: ClientType;
  type: InconsistencyType;
  severity: InconsistencySeverity;
  title: string;
  description: string;
  detectedAt: string;
  affectedDocuments: string[];
  expectedDocuments?: string[];
  recommendation: string;
  autoFixable: boolean;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface ConsistencyCheckStats {
  totalClients: number;
  clientsWithIssues: number;
  totalInconsistencies: number;
  criticalIssues: number;
  highIssues: number;
  mediumIssues: number;
  lowIssues: number;
  autoFixableIssues: number;
  resolvedIssues: number;
}

// Client File Chat types
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  citations?: DocumentCitation[];
}

export interface DocumentCitation {
  documentId: string;
  documentName: string;
  pageNumber?: number;
  excerpt: string;
  relevanceScore: number;
  source?: string;
  sourceUrl?: string;
}

export interface ClientFileChatRequest {
  clientId: string;
  message: string;
  conversationHistory?: ChatMessage[];
}

export interface ClientFileChatResponse {
  message: ChatMessage;
  sources: DocumentCitation[];
}
